import { unreadable } from './shared.ts';

/**
 * §8's Subscriptions page.
 *
 * The `notified` sentence is the one that had to be written carefully rather than briefly. The obvious
 * copy — "30 new videos" — is false in both directions, and `subscriptions/feed.ts` has the
 * measurements: the flag predates the reader's last visit by up to seventeen days, and the single
 * newest upload in the feed was not carrying it. So the sentence says what the mark *means* — which is
 * about the reader's own feed — and stops there.
 */
export const subscriptions = {
  notice: {
    noSubscriptions:
      'This account follows no channels yet, so the feed has nothing to draw from. Open a channel and press Subscribe.',
  },

  /** The sub/unsub control, on the channel head and the Watch byline (`SubscribeButton`). */
  button: {
    subscribe: 'Subscribe',
    unsubscribe: 'Unsubscribe',
    signedOutTitle: 'Sign in to your instance to subscribe, and you will come straight back here',
    /** The measured refusal: the endpoint validates existence, so any deleted channel answers it. */
    invalidChannel: {
      title: 'Could not subscribe',
      detail: 'The instance says this channel does not exist or was deleted.',
      retryable: false,
    },
    failed: {
      title: 'Could not change the subscription',
      detail: 'The instance did not accept the change. Nothing was altered, so it is safe to try again.',
      retryable: true,
    },
  },

  /** One slot, and the instance's claim wins over ours — the progress bar underneath is untouched. */
  unseen: {
    label: 'Unseen',
    title:
      'Marked unseen by your instance. It appears when a channel publishes and clears when you open your subscriptions on the instance itself, so it is not "new since your last visit here".',
  },

  feed: {
    allDropped: 'The instance answered, and nothing in it could be shown.',
    allDroppedTitle: 'Could not read this feed',
    emptyNoChannels: {
      title: 'You follow no channels yet',
      detail:
        'The feed is built from your subscriptions on the instance, so it is empty until there is something to build it from.',
    },
    emptyNoVideos: {
      title: 'Nothing in the feed yet',
      detail:
        'Your subscriptions are there, but there is nothing in the feed for them. The instance builds it on a schedule, so a freshly-added channel can take a while to appear.',
    },
  },

  page: {
    heading: 'Subscriptions',
    lede: 'What the channels in your instance account have published, newest first.',
    loading: 'Loading your subscription feed…',
    gridLabel: 'Subscription feed',
    loadMore: (soFar: number): string => `Load more (${soFar} so far)`,
    loadingMore: 'Loading…',
    manage: 'Manage',
    /** The seen filter as a menu (a setting, so it holds across visits) — search's filter shape. */
    watched: {
      label: 'Watched',
      shown: 'Shown',
      hidden: 'Hidden',
    },
  },

  /** The manage page — `/feed/channels`, reached from the feed's Manage button. */
  manage: {
    heading: 'Manage subscriptions',
    lede: 'Add channels, or unsubscribe with a tile’s own button.',
    /** The way back to the feed this page manages — the header's far end. */
    done: 'Done',
    add: {
      heading: 'Add a subscription',
      label: 'Channel',
      placeholder: 'A channel URL, @handle or channel ID, or a name to search for',
      clear: 'Clear the box',
      submit: 'Find',
      searching: 'Looking…',
      resultsLabel: 'Found channels',
      nothing: (q: string): string => `Nothing found for “${q}”.`,
      notResolvable: 'That link does not name a channel.',
      autoGenerated: 'Auto-generated',
    },
    list: {
      following: (count: number): string => `Following ${count}`,
      loading: 'Loading your subscriptions…',
      empty: 'No subscriptions yet. Add one above.',
      filterLabel: 'Filter your subscriptions',
      filterPlaceholder: 'Filter by name',
      filterClear: 'Clear the filter',
      /** The collapsed icon's name while a filter is set — the reason the grid looks short. */
      filterActiveTitle: (q: string): string => `Filtering by “${q}”`,
      /** Distinct from `empty` on purpose: an emptied grid otherwise reads as "no subscriptions". */
      noMatches: (q: string): string => `No subscription matches “${q}”.`,
      /** Sort, right of the search icon. Names only — counts hydrate too late to sort by. */
      sort: {
        label: 'Sort',
        listOrder: 'List order',
        nameAsc: 'Name A–Z',
        nameDesc: 'Name Z–A',
      },
      /** The in-bar icon while the search is open — pressing it folds the bar away. */
      filterDone: 'Done filtering',
      subscribers: (count: string): string => `${count} subscribers`,
      /** A channel `isChannelGone` matched — the tile's meta slot, and its tooltip. */
      gone: 'Deleted or terminated',
      goneTitle: (name: string): string =>
        `YouTube no longer serves ${name}. The channel was deleted or terminated, and unsubscribing is the only change left to make here.`,
      /** Each tile's own two-click Unsubscribe — the selection is retired. */
      unsubscribeOne: (name: string): string => `Unsubscribe from ${name}`,
      removing: 'Unsubscribing…',
      removeFailed: {
        title: 'Could not unsubscribe',
        detail:
          'The instance refused the change. The list below shows what it holds now, and trying again is safe.',
        retryable: true,
      },
    },
  },

  problem: {
    feedNotFound: {
      title: 'This instance has no subscription feed',
      detail: 'It is not served here. Some instances leave parts of the app out.',
      retryable: false,
    },
    feedUnreadable: unreadable('Could not read the feed'),
    feedDisabled: {
      title: 'The subscription feed is switched off on this instance',
      detail:
        'Its administrator turned it off. Your subscriptions themselves are unaffected, and the list below still works.',
      retryable: false,
    },
    listNotFound: {
      title: 'This instance has no subscription list',
      detail: 'The channels you follow cannot be read here.',
      retryable: false,
    },
    listUnreadable: unreadable('Could not read your subscriptions'),
  },
} as const;
